/**
 * Meta Tags Generation Module
 * Handles page titles, descriptions, OG tags, and Twitter cards
 * Part of SEO optimization - follows AIM atomicity principle
 */

/**
 * Generate page title with brand
 */
export const generatePageTitle = (pageTitle, includesBrand = true) => {
  const brand = 'TrckIt'
  if (!pageTitle) return brand
  return includesBrand ? `${pageTitle} | ${brand}` : pageTitle
}

/**
 * Generate meta description
 */
export const generateMetaDescription = (description, maxLength = 160) => {
  if (!description) return 'Track and find mobile vendors like food trucks, ice cream vendors, and mobile services in your area with TrckIt'
  return description.length > maxLength 
    ? description.substring(0, maxLength - 3) + '...' 
    : description
}

/**
 * Generate canonical URL
 */
export const generateCanonicalUrl = (path) => {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'http://localhost:3001'
  return `${baseUrl}${path}`
}

/**
 * Generate Open Graph meta tags
 */
export const generateOGTags = (data) => {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'http://localhost:3001'
  
  return {
    'og:type': data.type || 'website',
    'og:title': data.title || 'TrckIt - Track Food Trucks & Mobile Vendors',
    'og:description': data.description || 'Track and find mobile entities like food trucks, ice cream vendors, and mobile services in your area',
    'og:image': data.image || `${baseUrl}/logo.png`,
    'og:url': data.url || baseUrl,
    'og:site_name': 'TrckIt',
  }
}

/**
 * Generate Twitter Card meta tags
 */
export const generateTwitterTags = (data) => {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'http://localhost:3001'
  
  return {
    'twitter:card': data.cardType || 'summary_large_image',
    'twitter:title': data.title || 'TrckIt - Track Food Trucks & Mobile Vendors',
    'twitter:description': data.description || 'Track and find mobile entities like food trucks, ice cream vendors, and mobile services in your area',
    'twitter:image': data.image || `${baseUrl}/logo.png`,
  }
}

/**
 * Generate hreflang links for multi-language support
 */
export const generateHreflangLinks = (path, languages = ['en', 'es']) => {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'http://localhost:3001'
  
  return languages.map(lang => ({
    rel: 'alternate',
    hreflang: lang,
    href: `${baseUrl}${path}?lang=${lang}`,
  }))
}

/**
 * Extract keywords from entity data
 */
export const generateKeywords = (entity) => {
  if (!entity) return 'food trucks, mobile vendors, tracking, location, find nearby'
  
  const keywords = [
    entity.name,
    entity.category,
    entity.type,
    'mobile vendor',
    'find nearby',
    'location tracking',
  ]

  const currentAddress = entity.currentAddress || entity.current_address
  if (currentAddress) {
    // Extract city/state if possible
    const addressParts = currentAddress.split(',')
    keywords.push(...addressParts.map(part => part.trim()))
  }

  return keywords.filter(Boolean).join(', ')
}
